import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { rowsParam } from '@/pages/catalogImport/notices';
import type { VariantMismatchField } from '@/pages/catalogImport/store';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RowDrawerVariantFixModel as VariantFix } from './rowDrawerResolutions';
import { RowDrawerVariantFixFigure } from './RowDrawerVariantFixFigure';

type RowDrawerVariantFixProps = {
    fix: VariantFix;
    onPick: (field: VariantMismatchField, value: number | null) => void;
    onApply: (field: VariantMismatchField) => void;
};

/**
 * A variant's differing figures inside the problem card
 * (`VARIANT_FIELDS_DIFFER`, bos-app#219): its title, then per figure (sale
 * price, wholesale price, minimum quantity) one sentence naming the
 * variant's rows, a radio card per value with the rows that carry it
 * ("Rows 177, 178"), the bulk fix's default marked **Suggested** and
 * picked until the user picks another, why it is suggested and that it
 * applies to those rows only, and **Use ₾ 50.00 for rows 177, 178**, which
 * gives the picked value to every row of the variant. A price reads as the
 * app prints money, the minimum as a count, a blank cell as "(blank)".
 */
export const RowDrawerVariantFix: React.FC<RowDrawerVariantFixProps> = ({
    fix,
    onPick,
    onApply,
}) => {
    const { t } = useTranslation('pages');
    const rows = rowsParam(fix.rows);

    return (
        <div
            className="mt-2 flex w-full flex-col gap-4"
            {...getNodeTestIdProp('catalog-import-row-drawer-variant-fix')}
        >
            <Typography type="body-sm" weight="medium">
                {t('catalogImport.products.drawer.fix.variantFields.title')}
            </Typography>
            {fix.figures.map((figure) => (
                <RowDrawerVariantFixFigure
                    figure={figure}
                    key={figure.field}
                    onApply={onApply}
                    onPick={onPick}
                    rows={rows}
                />
            ))}
        </div>
    );
};
